import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import DeltaChip from './DeltaChip';
import InlineTrend from './InlineTrend';
import { getMetricStatusColor } from './metricStatus';

interface VolumeCardProps {
  metricName: string;
  owner: string;
  goal: number;
  /** null entries = weeks with no observations (distinct from a real 0). */
  weeklyVolumes: Array<number | null>;
  weeklyAttainment: Array<number | null>;
  weekIndex: number;
  weeks: string[];
}

function formatVolume(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

const VolumeCard: React.FC<VolumeCardProps> = ({ metricName, owner, goal, weeklyVolumes, weeklyAttainment, weekIndex, weeks }) => {
  const inRange = weekIndex >= 0 && weekIndex < weeklyVolumes.length;
  const volume = inRange ? weeklyVolumes[weekIndex] : (weeklyVolumes[weeklyVolumes.length - 1] ?? null);
  // Previous week WITH data, so a gap never looks like "no change".
  let prevVolume: number | null = null;
  for (let i = Math.min(weekIndex, weeklyVolumes.length) - 1; i >= 0; i--) {
    const candidate = weeklyVolumes[i];
    if (typeof candidate === 'number') {
      prevVolume = candidate;
      break;
    }
  }
  const attainmentRaw = weeklyAttainment.length > 0 ? weeklyAttainment[weekIndex] ?? null : null;
  const hasAttainment = weeklyAttainment.length > 0 && attainmentRaw !== null;
  const attainment = attainmentRaw ?? 0;
  const noData = volume === null;
  const color = noData ? '#8c8c94' : hasAttainment ? getMetricStatusColor(attainment, goal) : '#414d5c';

  return (
    <div className="volume-card" style={{ borderLeftColor: color }}>
      <Box variant="p" fontSize="body-s" fontWeight="bold" color="text-label">
        {metricName}
      </Box>

      <div className="volume-card__body">
        <div className="volume-card__numbers">
          {noData ? (
            <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
              <span style={{ color }}>—</span>
            </Box>
          ) : hasAttainment ? (
            <>
              <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
                <span style={{ color }}>{attainment.toFixed(0)}%</span>
              </Box>
              <Box variant="p" fontSize="body-s" color="text-body-secondary">
                {formatVolume(volume)}
              </Box>
            </>
          ) : (
            <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
              {formatVolume(volume)}
            </Box>
          )}
        </div>
        {hasAttainment ? (
          <InlineTrend values={weeklyAttainment} goal={goal} color={color} selectedIndex={weekIndex} weekLabels={weeks} />
        ) : (
          <InlineTrend values={weeklyVolumes} color={color} formatValue={(v) => `${(v / 1000).toFixed(0)}K`} selectedIndex={weekIndex} weekLabels={weeks} />
        )}
      </div>

      <div className="volume-card__footer">
        <DeltaChip current={volume} previous={prevVolume} unit="volume" />
        <Box variant="span" fontSize="body-s" color="text-status-inactive">
          Owner: {owner}{goal > 0 ? ` · Goal: ≥ ${goal}%` : ''}
        </Box>
      </div>
    </div>
  );
};

export default VolumeCard;
